import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutGrid, Mail, Briefcase, TrendingUp, Activity,
  Search, Settings, ScrollText, LogOut,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import {
  CommandPalette, CommandPaletteTrigger, useCommandPalette,
} from "@/components/CommandPalette";

const NAV = [
  { label: "Pipeline Overview", path: "/",              icon: LayoutGrid },
  { label: "Emails",            path: "/emails",        icon: Mail },
  { label: "Opportunities",     path: "/opportunities", icon: Briefcase },
  { label: "Business Value",    path: "/value",         icon: TrendingUp },
  { label: "Operations",        path: "/operations",    icon: Activity },
  { label: "Global Search",     path: "/search",        icon: Search },
  { label: "Settings",          path: "/settings",      icon: Settings },
  { label: "Audit Log",         path: "/audit",         icon: ScrollText },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const { open: cmdkOpen, setOpen: setCmdkOpen } = useCommandPalette();

  return (
    <div className="min-h-screen flex bg-background">
      <CommandPalette open={cmdkOpen} onOpenChange={setCmdkOpen} />
      <aside
        data-testid="sidebar"
        className="w-64 border-r border-border bg-card h-screen sticky top-0 flex flex-col"
      >
        <div className="px-5 py-5 border-b border-border">
          <div className="flex items-baseline gap-2">
            <span className="font-serif text-2xl tracking-tight leading-none">Prism</span>
          </div>
          <div className="text-[10px] text-muted-foreground mt-1 italic font-serif">
            Refract your inbox into revenue
          </div>
        </div>

        <div className="px-3 pt-3">
          <CommandPaletteTrigger onOpen={() => setCmdkOpen(true)} />
        </div>

        <nav className="flex-1 py-3 px-2 space-y-0.5">
          {NAV.map((n) => {
            const Icon = n.icon;
            const active = loc.pathname === n.path || (n.path !== "/" && loc.pathname.startsWith(n.path));
            return (
              <NavLink
                key={n.path}
                to={n.path}
                data-testid={`nav-${n.path.replace(/\//g, "") || "home"}`}
                className={() =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                    active
                      ? "bg-zinc-900 text-white"
                      : "text-zinc-700 hover:bg-zinc-100 hover:text-zinc-900"
                  }`
                }
              >
                <Icon size={16} strokeWidth={1.75} />
                <span className="font-medium tracking-tight">{n.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t border-border p-3">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="h-8 w-8 rounded-full bg-zinc-900 text-white grid place-items-center text-xs font-mono">
              {(user?.email || "A").slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium truncate" data-testid="user-email">{user?.email}</div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{user?.role}</div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="p-1.5 rounded-md text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
              title="Log out"
            >
              <LogOut size={14} />
            </button>
          </div>
          <div className="mt-2 pt-2 border-t border-border flex items-center gap-2 px-2">
            <span className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground">Powered by</span>
            <img
              src="https://customer-assets.emergentagent.com/job_metrics-hub-190/artifacts/fg8v3tuh_rtslogo-with-name-transparent-300-ppi-RGB.png"
              alt="Resolve Tech Solutions"
              className="h-7 w-auto object-contain"
              data-testid="powered-by-rts"
            />
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        {children}
      </main>
    </div>
  );
}
